import { Notifications } from './Notifications';
import { useState } from 'react';
import { Link, NavLink, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Menu from '@radix-ui/react-dropdown-menu';
import { Activity, Boxes, FolderKanban, LogOut, MessageSquare, MoreHorizontal, PanelLeftClose, PanelLeftOpen, Pencil, Plug, Plus, Search, Settings, Trash2, ShieldCheck, Gem, Gauge, Coins, Moon, Sun, Monitor } from 'lucide-react';
import { api } from '../../lib/api';
import { groupByDate, cx } from '../../lib/format';
import type { Conversation, Preferences } from '../../types/api';
import { useAuth } from '../auth/AuthProvider';
import { IconButton } from '../../components/ui';
import { Logo } from './Logo';
import { useAccount } from '../plans/useAccount';
import { UsageMeter } from '../plans/UsageMeter';
import { Compass } from 'lucide-react';
import '../explore/explore.css';

export function Sidebar({ collapsed, onToggle, onNavigate }: { collapsed: boolean; onToggle: () => void; onNavigate?: () => void }) {
  const { user, logout } = useAuth();
  const account = useAccount();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { id: activeId } = useParams();
  const [query, setQuery] = useState('');
  const convs = useQuery({
    queryKey: ['conversations', query],
    queryFn: () => api<{ conversations: Conversation[] }>(`/api/conversations${query ? `?q=${encodeURIComponent(query)}` : ''}`).then((r) => r.conversations),
    placeholderData: (p) => p,
  });
  const setTheme = useMutation({
    mutationFn: (theme: Preferences['theme']) => api('/api/preferences', { method: 'PATCH', body: { theme } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['preferences'] }),
  });

  if (collapsed) {
    return (
      <aside className="sidebar sidebar--collapsed" aria-label="Sidebar">
        <IconButton label="Expand sidebar" onClick={onToggle}><PanelLeftOpen size={18} /></IconButton>
        <IconButton label="New chat" onClick={() => nav('/')}><Plus size={18} /></IconButton>
        <IconButton label="Explore" onClick={() => nav('/explore')}><Compass size={19} /></IconButton>
        <IconButton label="Projects" onClick={() => nav('/projects')}><FolderKanban size={18} /></IconButton>
        <IconButton label="MCP connectors" onClick={() => { nav('/connectors'); onNavigate?.(); }}><Plug size={18} /></IconButton>
        <IconButton label="Artifacts" onClick={() => nav('/artifacts')}><Boxes size={18} /></IconButton>
        <IconButton label="Activity" onClick={() => nav('/activity')}><Activity size={18} /></IconButton><Notifications />
      </aside>
    );
  }

  return (
    <aside className="sidebar" aria-label="Sidebar">
      <div className="sidebar__top">
        <Link to="/" className="sidebar__brand" onClick={onNavigate}><Logo /></Link><Notifications />
        <IconButton label="Collapse sidebar" size="sm" onClick={onToggle}><PanelLeftClose size={17} /></IconButton>
      </div>
      <button className="sidebar__new" onClick={() => { nav('/'); onNavigate?.(); }}>
        <Plus size={16} /> New chat <kbd>⌘K</kbd>
      </button>
      <nav className="sidebar__nav">
        <NavLink to="/explore" className="sidebar__explore" onClick={onNavigate}><Compass size={18} /> Explore</NavLink>
        <NavLink to="/projects" onClick={onNavigate}><FolderKanban size={16} /> Projects</NavLink>
        <NavLink to="/artifacts" onClick={onNavigate}><Boxes size={16} /> Artifacts</NavLink>
        <NavLink to="/connectors" onClick={onNavigate}><Plug size={16} /> MCP connectors</NavLink>
        <NavLink to="/chats" onClick={onNavigate}><MessageSquare size={16} /> All chats</NavLink>
        <NavLink to="/activity" onClick={onNavigate}><Activity size={16} /> Agent activity</NavLink>
        <NavLink to="/credit-usage" onClick={onNavigate}><Coins size={16} /> Credit usage</NavLink>
        <NavLink to="/app-usage" onClick={onNavigate}><Gauge size={16} /> App usage</NavLink>
        <NavLink to="/plans" onClick={onNavigate}><Gem size={16} /> Plans</NavLink>
        {account.data?.admin.eligible && <NavLink to="/admin" onClick={onNavigate}><ShieldCheck size={16} /> Admin</NavLink>}
      </nav>
      <label className="sidebar__search">
        <Search size={14} />
        <input placeholder="Search chats" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search chats" />
      </label>
      <div className="sidebar__list">
        {groupByDate(convs.data ?? []).map(([label, items]) => (
          <div key={label} className="sidebar__group">
            <div className="sidebar__label">{label}</div>
            {items.map((c) => <ConversationItem key={c.id} c={c} active={c.id === activeId} onNavigate={onNavigate} />)}
          </div>
        ))}
        {convs.data && !convs.data.length && <p className="sidebar__empty">{query ? 'No matching chats.' : 'Your conversations will appear here.'}</p>}
      </div>
      <UsageMeter />
      <Menu.Root>
        <Menu.Trigger asChild>
          <button className="sidebar__profile">
            <span className="avatar">{user?.displayName.slice(0, 1).toUpperCase()}</span>
            <span className="sidebar__profile-name">{user?.displayName}<small>{user?.email}</small></span>
          </button>
        </Menu.Trigger>
        <Menu.Portal>
          <Menu.Content className="menu" side="top" align="start" sideOffset={6}>
            <Menu.Item className="menu__item" onSelect={() => nav('/settings')}><Settings size={15} /> Settings</Menu.Item>
            <Menu.Separator className="menu__sep" />
            <Menu.Label className="menu__label">Theme</Menu.Label>
            <Menu.Item className="menu__item" onSelect={() => setTheme.mutate('light')}><Sun size={15} /> Light</Menu.Item>
            <Menu.Item className="menu__item" onSelect={() => setTheme.mutate('dark')}><Moon size={15} /> Dark</Menu.Item>
            <Menu.Item className="menu__item" onSelect={() => setTheme.mutate('system')}><Monitor size={15} /> System</Menu.Item>
            <Menu.Separator className="menu__sep" />
            <Menu.Item className="menu__item" onSelect={() => void logout().then(() => nav('/login'))}><LogOut size={15} /> Log out</Menu.Item>
          </Menu.Content>
        </Menu.Portal>
      </Menu.Root>
    </aside>
  );
}

function ConversationItem({ c, active, onNavigate }: { c: Conversation; active: boolean; onNavigate?: () => void }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(c.title);
  const rename = useMutation({
    mutationFn: (t: string) => api(`/api/conversations/${c.id}`, { method: 'PATCH', body: { title: t } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['conversations'] }),
  });
  const remove = useMutation({
    mutationFn: () => api(`/api/conversations/${c.id}`, { method: 'DELETE' }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['conversations'] }); if (active) nav('/'); },
  });
  if (editing) {
    return (
      <form className="sidebar__item is-editing" onSubmit={(e) => { e.preventDefault(); setEditing(false); if (title.trim() && title !== c.title) rename.mutate(title.trim()); }}>
        <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} onBlur={() => setEditing(false)} aria-label="Conversation title" />
      </form>
    );
  }
  return (
    <div className={cx('sidebar__item', active && 'is-active')}>
      <Link to={`/chat/${c.id}`} onClick={onNavigate} title={c.title}>{c.title}</Link>
      <Menu.Root>
        <Menu.Trigger asChild><button className="sidebar__item-menu" aria-label={`Options for ${c.title}`}><MoreHorizontal size={15} /></button></Menu.Trigger>
        <Menu.Portal>
          <Menu.Content className="menu" align="start" sideOffset={4}>
            <Menu.Item className="menu__item" onSelect={() => { setTitle(c.title); setEditing(true); }}><Pencil size={14} /> Rename</Menu.Item>
            <Menu.Item className="menu__item menu__item--danger" onSelect={() => remove.mutate()}><Trash2 size={14} /> Delete</Menu.Item>
          </Menu.Content>
        </Menu.Portal>
      </Menu.Root>
    </div>
  );
}
